import { z } from 'zod';
import { submissionQueueItemSchema, submissionStatusSchema, validationLogSchema, } from '../schemas/submission.js';
import { idParamSchema, listQueryBase, multiFilter, paginatedResponse, reasonSchema, } from './_shared.js';
/**
 * Maximum allowed multipart zip body for `POST /api/submissions` (spec §5.1).
 * Bytes — 10 MiB. Single source of truth so the API server, the CLI, and any
 * future client all enforce the same limit. Drift between client + server
 * here would mean a user sees either a generic `PAYLOAD_TOO_LARGE` after a
 * multi-MB upload, or a confusing local rejection that the server would have
 * accepted.
 */
export const MAX_SUBMISSION_BYTES = 10 * 1024 * 1024;
const SUBMISSION_STATUS = [
    'validating',
    'validation_failed',
    'upload_failed',
    'scan_failed',
    'pending_review',
    'approving',
    'pr_opened',
    'approved',
    'rejected',
];
const SUBMISSION_TYPE = ['zip', 'pr'];
/**
 * `GET /api/submissions` — paginated submissions list (T16a). The route is
 * auth-only; row-level scoping happens in the service layer:
 *
 *   - Caller WITH `submissions:view` (admin): unrestricted list. `submitterId`
 *     / `teamId` / `categoryId` filters are honoured as-supplied.
 *   - Caller WITHOUT `submissions:view`: auto-scoped to rows where
 *     `submitter_id = principal.id`. The `submitterId` filter is still
 *     accepted but trumped by the auto-scope — if the URL asks for another
 *     user's submissions the page returns empty (not 403).
 *
 * `status` is a comma-joined multi-filter over the 9 canonical states; absent
 * → all states. `type` is a comma-joined multi-filter over `zip`/`pr`.
 * `submitterId` / `teamId` / `categoryId` are single UUIDs. The flat
 * `submissions:view` precondition that previously gated the route has been
 * dropped — the row-level scope replaces it. See `marketplace-api/CLAUDE.md`
 * "Row-level scoping pattern" for the canonical write-up.
 */
export const listSubmissionsQuerySchema = listQueryBase.extend({
    status: multiFilter(SUBMISSION_STATUS),
    type: multiFilter(SUBMISSION_TYPE),
    submitterId: z.string().uuid().optional(),
    teamId: z.string().uuid().optional(),
    categoryId: z.string().uuid().optional(),
});
/**
 * Export query: same filters + sort as the list query but without pagination.
 * Used by `GET /api/submissions/export` to stream all matching rows as CSV.
 * Reused by MySubmissions (Plan 3 Task 10) with `submitterId` pre-bound on
 * the UI side — no separate `my_submissions_export` endpoint is needed.
 */
export const exportSubmissionsQuerySchema = listSubmissionsQuerySchema.omit({
    page: true,
    pageSize: true,
});
export const listSubmissionsResponseSchema = paginatedResponse(submissionQueueItemSchema);
/**
 * Multipart fields companion for `POST /api/submissions`. Only the non-file
 * fields are typed here; the zip itself is parsed by `@fastify/multipart`
 * and validated separately.
 */
export const createSubmissionFieldsSchema = z
    .object({
    teamId: z.string().uuid().optional(),
})
    .strict();
/** `POST /api/submissions` returns 202 with status-poll URL. */
export const createSubmissionResponseSchema = z.object({
    submissionId: z.string().uuid(),
    statusUrl: z.string().min(1),
});
/** Re-export of the canonical id-param schema for naming clarity at call sites. */
export const submissionIdParamSchema = idParamSchema;
/**
 * `GET /api/submissions/:id` returns the camelCase wire shape — the route
 * remaps from snake_case DB columns. `validationLog` is the canonical
 * `{ ok, issues, pluginMeta? }` produced by `@cotality/marketplace-validator`
 * (mirrored in `validationLogSchema`); typing it explicitly lets CLI / UI
 * consumers walk `.issues[].severity` without a defensive double-parse.
 */
export const getSubmissionResponseSchema = z.object({
    id: z.string().uuid(),
    submitterId: z.string().uuid(),
    teamId: z.string().uuid().nullable(),
    pluginId: z.string().uuid().nullable(),
    type: z.enum(['zip', 'pr']),
    status: submissionStatusSchema,
    validationLog: validationLogSchema.nullable(),
    version: z.string().nullable(),
    adoPrUrl: z.string().url().nullable(),
    submittedAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** `POST /api/submissions/:id/approve` body — optional override of the
 * validator-resolved target repo. */
export const approveSubmissionBodySchema = z
    .object({
    targetRepo: z.string().min(1).optional(),
})
    .strict();
/**
 * Lifecycle ack shape — used by approve/reject/requeue. Carries the new
 * status so the admin UI can render an in-place row update without a
 * second request.
 */
export const submissionLifecycleResponseSchema = z.object({
    submissionId: z.string().uuid(),
    status: submissionStatusSchema,
});
export const approveSubmissionResponseSchema = submissionLifecycleResponseSchema;
export const rejectSubmissionBodySchema = z
    .object({
    reason: reasonSchema,
})
    .strict();
export const rejectSubmissionResponseSchema = submissionLifecycleResponseSchema;
export const requeueSubmissionResponseSchema = submissionLifecycleResponseSchema;
//# sourceMappingURL=submissions.js.map